import styled, { css } from 'styled-components/macro'
import type { FhModalIntent, FhModalSize } from './types'

/**
 * Token-driven styles for {@link FhModal}.
 *
 * Every color / radius / shadow / spacing / type value resolves to a Flowhub
 * Design System CSS variable (emitted app-wide in `GlobalStyle` via
 * `dsTokens`). No hard-coded hexes are used where a token exists, so a theme
 * change propagates here automatically.
 *
 * Reference: FLOWHUB_DESIGN_LIBRARY §4.1 (modal) + §4.4 (pill buttons).
 */

/** Per-size card max-width. Mirrors the POS modal widths. */
const sizeMaxWidth: Record<FhModalSize, string> = {
  xs: '380px',
  sm: '480px',
  md: '640px',
  lg: '760px',
  xl: '900px',
  full: 'min(1040px, calc(100vw - var(--space-16)))',
}

/**
 * Per-intent theming (§4.1): icon-tile tint + the fill of the default
 * primary footer action. Hover uses the DS hover token where one exists;
 * danger/info darken via filter (no dedicated hover token).
 */
const intentTheme: Record<
  FhModalIntent,
  {
    tileBg: string
    tileFg: string
    actionBg: string
    actionFg: string
    actionHover: string
  }
> = {
  neutral: {
    tileBg: 'var(--surface-sunken)',
    tileFg: 'var(--gray-700)',
    actionBg: 'var(--action-primary)',
    actionFg: 'var(--action-on-primary)',
    actionHover: 'background: var(--action-primary-hover);',
  },
  primary: {
    tileBg: 'var(--surface-brand-soft)',
    /* --text-heading, not the --deep-purple primitive: pearl value is
       identical (#20026C) but dark themes lift it so the tile glyph stays
       visible on the dark brand-soft wash. */
    tileFg: 'var(--text-heading)',
    actionBg: 'var(--action-primary)',
    actionFg: 'var(--action-on-primary)',
    actionHover: 'background: var(--action-primary-hover);',
  },
  commerce: {
    tileBg: 'var(--moss-wash)',
    tileFg: 'var(--status-success-ink)',
    actionBg: 'var(--action-accent)',
    actionFg: 'var(--action-on-accent)',
    actionHover: 'background: var(--action-accent-hover);',
  },
  danger: {
    tileBg: 'var(--status-danger-soft)',
    tileFg: 'var(--status-danger)',
    actionBg: 'var(--status-danger)',
    /* On-fill ink token, not --white: dark themes lift the danger fill and
       flip this to a dark ink (white on lifted #ff7a45 is ~2.6:1). Pearl
       value IS white — pixel-identical. */
    actionFg: 'var(--status-danger-ink)',
    actionHover: 'filter: brightness(0.92);',
  },
  info: {
    tileBg: 'var(--status-info-soft)',
    tileFg: 'var(--status-info)',
    actionBg: 'var(--action-info)',
    /* Same on-fill pattern as danger (see --status-info-ink in dsTokens). */
    actionFg: 'var(--status-info-ink)',
    actionHover: 'filter: brightness(0.92);',
  },
}

/**
 * Full-viewport scrim. Centers the card and dims the page with `--scrim`
 * (rgba(20,2,76,.34)). Backdrop blur is `none` — a deliberate **global**
 * deviation from the §4.1 canonical `blur(2px)`. Empirically `blur(1px)`
 * still left inventory SKU text (e.g. sidebar identifiers) too soft to
 * double-check; only the tint remains so background copy stays readable
 * behind every modal. Shared by ~130 ManageModal sites; needs design
 * sign-off. Do not restore 2px without it.
 */
export const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-10);
  background: var(--scrim);
  backdrop-filter: none; /* §4.1 deviation: was blur(2px); 1px still illegible */
  -webkit-backdrop-filter: none;
  animation: fh-modal-scrim-in var(--dur-base) var(--ease-out);

  @keyframes fh-modal-scrim-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

/**
 * Centered rounded card surface. §4.1 heavy modal lift (`--shadow-modal`,
 * deep-purple tinted), `--radius-lg` (24px) corners, white surface.
 */
export const Card = styled.div<{ $size: FhModalSize }>`
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  max-width: ${({ $size }) => sizeMaxWidth[$size]};
  max-height: calc(100vh - var(--space-20));
  background: var(--surface-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-modal);
  overflow: hidden;
  font-family: var(--font-sans);
  color: var(--text-body);
  outline: none; /* card receives fallback focus; ring lives on controls */
  animation: fh-modal-card-in var(--dur-base) var(--ease-out);

  @keyframes fh-modal-card-in {
    from {
      opacity: 0;
      transform: translateY(8px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`

/** Header slot — icon tile + title/subtitle, or custom content. */
export const Header = styled.header`
  flex: none;
  padding: var(--space-8) var(--space-8) var(--space-5);
  /* Leave room for the round close affordance. */
  padding-inline-end: var(--space-16);
`

/** Lays the intent icon tile beside the title/subtitle stack. */
export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-4);
`

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
`

/**
 * §4.1 icon tile: 46px square, radius 13px (spec-fixed, sits between
 * `--radius-control` and `--radius-md`), tinted by intent.
 */
export const IconTile = styled.div<{ $intent: FhModalIntent }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 13px;
  background: ${({ $intent }) => intentTheme[$intent].tileBg};
  color: ${({ $intent }) => intentTheme[$intent].tileFg};
`

export const Title = styled.h2`
  margin: 0;
  font-family: var(--font-sans);
  font-weight: var(--weight-median);
  font-size: var(--text-xl);
  line-height: var(--leading-normal);
  letter-spacing: var(--tracking-tight);
  color: var(--text-heading);
`

export const Subtitle = styled.p`
  margin: 0;
  font-family: var(--font-sans);
  font-weight: var(--weight-neutral);
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
  color: var(--text-muted);
`

/**
 * Body slot — scrolls independently when content overflows.
 * Headerless dialogs (custom in-body headings) get top padding so content
 * clears the card edge / close affordance; a preceding Header already
 * carries that spacing, so the padding collapses to 0 after one.
 */
export const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-8) var(--space-8) var(--space-8);

  header + & {
    padding-top: 0;
  }
  font-family: var(--font-sans);
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
  color: var(--text-body);
`

/**
 * Footer slot — actions row with a DS hairline divider above.
 * `$split` (structured Cancel present) pins Cancel left, actions right
 * (§4.1 Cancel-left / primary-right).
 */
export const Footer = styled.footer<{ $split?: boolean }>`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: ${({ $split }) => ($split ? 'space-between' : 'flex-end')};
  gap: var(--space-3);
  padding: var(--space-5) var(--space-8);
  border-top: 1px solid var(--border-subtle);
`

/** Right-aligned cluster for secondary + primary actions. */
export const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
`

/** Shared pill-button base — §4.1 modal control metric (54px, the §4.4 large rung). */
const footerButtonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 54px;
  padding: 0 var(--space-6);
  border: none;
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: var(--text-base);
  font-weight: var(--weight-median);
  line-height: var(--leading-normal);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out),
    color var(--dur-fast) var(--ease-out),
    filter var(--dur-fast) var(--ease-out);

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  &:disabled {
    background: var(--gray-400);
    color: var(--white);
    filter: none;
    cursor: not-allowed;
  }
`

/** Filled primary action — fill themed by intent (§4.1 footer rule). */
export const PrimaryButton = styled.button<{ $intent: FhModalIntent }>`
  ${footerButtonBase};
  background: ${({ $intent }) => intentTheme[$intent].actionBg};
  color: ${({ $intent }) => intentTheme[$intent].actionFg};

  &:hover:not(:disabled) {
    ${({ $intent }) => intentTheme[$intent].actionHover};
  }
`

/** Outline button — secondary actions and Cancel (§4.4 Outline variant). */
export const OutlineButton = styled.button`
  ${footerButtonBase};
  background: var(--surface-card);
  border: 1.5px solid var(--border-brand);
  /* --text-heading (pearl = deep-purple) so the label re-themes with the
     card surface instead of vanishing on dark cards. */
  color: var(--text-heading);

  &:hover:not(:disabled) {
    background: var(--surface-page);
  }

  &:disabled {
    background: var(--surface-sunken);
    border-color: var(--border-subtle);
    color: var(--gray-400);
  }
`

/**
 * Round close affordance, top-right. §4.1: sits on a `--surface-page`
 * (pearly-white) wash; deep-purple ink on hover.
 */
export const CloseButton = styled.button`
  position: absolute;
  top: var(--space-5);
  inset-inline-end: var(--space-5);
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--space-10);
  height: var(--space-10);
  padding: 0;
  border: none;
  border-radius: var(--radius-circle);
  background: var(--surface-page);
  color: var(--text-muted);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out),
    color var(--dur-fast) var(--ease-out);

  &:hover {
    /* --surface-brand-soft (pearl = --pearl) so the hover wash re-themes
       alongside the --text-heading ink. */
    background: var(--surface-brand-soft);
    color: var(--text-heading);
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`
